import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';

/**
 * The menu: items, the categories they sit in, and the printers a category
 * sends its ticket to.
 *
 * One place for the HTTP so the screens stay about layout. Every call carries
 * the token the same way the rest of the app does — the value in the store
 * already includes its scheme, so it is passed through untouched.
 */

const auth = (token) => ({ headers: { Authorization: token } });

/**
 * Items and categories arrive together.
 *
 * `/menu/get-business-menu-items` answers with both, which is the phone's own
 * call — one request instead of two, and the two lists can never be a moment
 * out of step with each other.
 *
 * Note the server filters this list to `status: 'available'`. A deleted item is
 * not gone, it is unavailable, and it simply stops coming back.
 */
export async function fetchMenu({ businessId, token }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/menu/get-business-menu-items/${businessId}`,
    auth(token)
  );
  return {
    menuItems: data?.data?.menuItems || [],
    categories: data?.data?.categories || [],
  };
}

export async function createMenuItem({ payload, token }) {
  const { data } = await axios.post(`${constants.baseUrl}/menu`, payload, auth(token));
  return data?.data?.createdMenuItem;
}

/**
 * The server whitelists what an update may touch — name, description, price,
 * category, status, imageUrl — and quietly drops anything else, so sending the
 * whole item back is safe.
 */
export async function updateMenuItem({ id, payload, token }) {
  const { data } = await axios.patch(`${constants.baseUrl}/menu/${id}`, payload, auth(token));
  return data?.data?.updatedMenuItem;
}

/**
 * A soft delete: the server sets `status: 'unavailable'` rather than dropping
 * the row, because orders already placed still point at it and a receipt
 * printed last week must keep its line.
 */
export async function deleteMenuItem({ id, token }) {
  const { data } = await axios.delete(`${constants.baseUrl}/menu/${id}`, auth(token));
  return data?.data?.deletedMenuItem;
}

/* ── categories ─────────────────────────────────────────────────────────── */

export async function fetchCategories({ businessId, token }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/menu-categories/get-business-categories/${businessId}`,
    auth(token)
  );
  return data?.data?.categories || [];
}

/**
 * `printer` is three-valued on the server: leave it out to keep what is there,
 * send null to unmap, send an id to point the category at a printer. The
 * dialogs always send one of the last two, because a form that shows a value
 * should write the value it shows.
 */
export async function createCategory({ payload, token }) {
  const { data } = await axios.post(`${constants.baseUrl}/menu-categories`, payload, auth(token));
  return data?.data?.createdCategory;
}

export async function updateCategory({ id, payload, token }) {
  const { data } = await axios.patch(
    `${constants.baseUrl}/menu-categories/${id}`,
    payload,
    auth(token)
  );
  return data?.data?.updatedCategory;
}

/** A hard delete, and the only one in this module. 204, no body. */
export async function deleteCategory({ id, token }) {
  await axios.delete(`${constants.baseUrl}/menu-categories/${id}`, auth(token));
}

/* Printers live in `containers/printing/printersApi.js` — the order pad
   needs them too, so they are not a menu concern. */

/* ── helpers the screens share ──────────────────────────────────────────── */

/** The category a menu item belongs to, or null — items may have none. */
export function categoryOf(item, categories) {
  if (!item?.category) return null;
  const id = typeof item.category === 'object' ? item.category._id : item.category;
  return categories.find((category) => category._id === id) || null;
}

/**
 * The printer id a category currently points at.
 *
 * The server populates `printer` on reads and stores a bare id on writes, so
 * the field is a document sometimes and a string other times. Both shapes are
 * read here rather than in three different components.
 */
export function printerIdOf(category) {
  const printer = category?.printer;
  if (!printer) return '';
  return typeof printer === 'object' ? printer._id || '' : printer;
}

export function printerNameOf(category, printers) {
  const id = printerIdOf(category);
  if (!id) return '';
  const printer = category?.printer;
  if (printer && typeof printer === 'object' && printer.name) return printer.name;
  const found = printers.find((entry) => entry._id === id);
  return found ? found.name : '';
}

/** Two letters, the way the phone draws a menu item with no picture. */
export function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
